import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { IAppLiveClock } from "./AppLiveFeed";

// AppReplyFocus - THE MESSAGE BOX KEEPS THE FOCUS IT WAS GIVEN WHILE THE CONVERSATION LOADS (2026-10-13).
//
// ⭐ WHAT WAS MEASURED (real Chrome, `capture-screens.mjs ONLY=rowclick ROW_REPEAT=30 ROW_DEBUG=1`, about 1 walk in 10):
// a reply-owed task row opens its chat AT the message the reply is owed for (`#c/<page>/m-<minute>`) and the app
// focuses the message box. ~50 ms later the box lost the focus with no `focus()` call anywhere in the app:
// `document.activeElement` was the conversation's IFRAME, and inside it the linked bubble. The frame's address carries
// the fragment (`blob:...#m-...`), the app makes every bubble a keyboard stop (`tabindex="0"`), and a browser that
// navigates to a fragment whose target can take the focus GIVES it the focus. Whether the bubble already had its
// tabindex when the browser looked decided each run - hence "intermittent".
//
// ⭐ THE RULE: for a short while after the box was focused FOR THE OWNER (`GuardMs`), a focus that arrives inside the
// frame WITHOUT the owner having pressed anything there is handed back to the box. The guard ends at the owner's first
// pointer press or key press in either document (a click into the conversation is theirs to make), or when the time
// is up. ⛔ FOCUS ONLY: nothing is typed, clicked or sent (decision 9).
// Shaped by interfaces, so the suite drives it with small fakes. A second consumer: any page that focuses a field
// beside a frame it navigates to a fragment.

/** The little of an event target this needs. */
export interface IReplyFocusTarget {
    addEventListener(Type: string, Listener: () => void, Capture?: boolean): void;
    removeEventListener(Type: string, Listener: () => void, Capture?: boolean): void;
}

export interface IReplyFocusFrame extends IReplyFocusTarget {
    contentDocument: (IReplyFocusTarget & { activeElement?: unknown; body?: unknown }) | null;
}

export interface IReplyFocusBox {
    focus(): void;
}

export class AppReplyFocus {
    public static readonly GuardMs: number = 3000;
    private Box: IReplyFocusBox;
    private Page: IReplyFocusTarget;
    private Clock: IAppLiveClock;
    private Frame: IReplyFocusFrame | null = null;
    private Inner: IReplyFocusTarget | null = null;
    private Timer: number = 0;
    private Done: boolean = false;
    /** How many times the focus was handed back (the suite reads it). */
    public Returned: number = 0;

    public constructor(Box: IReplyFocusBox, Page: IReplyFocusTarget, Clock: IAppLiveClock) {
        this.Box = Box;
        this.Page = Page;
        this.Clock = Clock;
    }

    /** Guard the box's focus while `Frame` loads its page. */
    public Watch = (Frame: IReplyFocusFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Done === true || this.Frame !== null) { return; }
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
            this.Page.addEventListener("pointerdown", this.Release, true);
            this.Page.addEventListener("keydown", this.Release, true);
            this.Timer = this.Clock.Later(AppReplyFocus.GuardMs, this.Release);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReplyFocus.Watch");
        }
    };

    /** The frame's page is in: its focus moves are heard, and a focus it already took (before `load`) is handed back. */
    private OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Done === true || this.Frame === null) { return; }
            const Doc: IReplyFocusFrame["contentDocument"] = this.Frame.contentDocument;
            if (Doc === null) { return; }
            this.Inner = Doc;
            Doc.addEventListener("focusin", this.OnFrameFocus, true);
            Doc.addEventListener("pointerdown", this.Release, true);
            Doc.addEventListener("keydown", this.Release, true);
            const Active: unknown = Doc.activeElement;
            if (Active !== null && Active !== undefined && Active !== Doc.body) { this.OnFrameFocus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReplyFocus.OnLoad");
        }
    };

    /** Something in the frame took the focus and the owner pressed nothing: the box has it back. */
    private OnFrameFocus = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Done === true) { return; }
            this.Returned = this.Returned + 1;
            this.Box.focus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReplyFocus.OnFrameFocus");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: ending the guard removes listeners and a timer - it must work whatever the gate says. */
    public Release = (): void => {
        try {
            if (this.Done === true) { return; }
            this.Done = true;
            if (this.Timer !== 0) { this.Clock.Cancel(this.Timer); this.Timer = 0; }
            this.Page.removeEventListener("pointerdown", this.Release, true);
            this.Page.removeEventListener("keydown", this.Release, true);
            if (this.Frame !== null) { this.Frame.removeEventListener("load", this.OnLoad); }
            if (this.Inner !== null) {
                this.Inner.removeEventListener("focusin", this.OnFrameFocus, true);
                this.Inner.removeEventListener("pointerdown", this.Release, true);
                this.Inner.removeEventListener("keydown", this.Release, true);
            }
            this.Frame = null; this.Inner = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReplyFocus.Release");
        }
    };
}
